import { IHttpApi } from '@cian/http-api';
import { useCallback, useEffect, useState } from 'react';
import { fetchChangeStatus, IChangeStatusResponse400 } from 'shared/repositories/cian-dobro/v1/change-status';
import {
  fetchGetRealEstateList,
  IGetRealEstateListResponse200,
  IGetRealEstateListResponse400,
} from 'shared/repositories/cian-dobro/v1/get-real-estate-list';
import { EStatus, IOffer } from 'shared/types/listing';
import { useApplicationContext } from 'shared/utils/applicationContext';

export type TLoadDataResult = { offers: IOffer[] } | 'error' | 'no_access';

async function loadData(httpApi: IHttpApi): Promise<TLoadDataResult> {
  try {
    const res = await fetchGetRealEstateList<IGetRealEstateListResponse200, IGetRealEstateListResponse400>({ httpApi });

    if (res.statusCode === 400) {
      return 'no_access';
    }

    return res.response;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err);

    return 'error';
  }
}

async function updateData(httpApi: IHttpApi, upd: IUpdateOffer) {
  try {
    const res = await fetchChangeStatus<IChangeStatusResponse400>({
      httpApi,
      parameters: upd,
    });

    if (res.statusCode === 400) {
      throw new Error('no access');
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err);

    throw err;
  }
}

export interface IUpdateOffer {
  id: number;
  status: EStatus;
  comment: string;
}

export function useFetchDobroListing() {
  const { httpApi } = useApplicationContext();
  const [result, setResult] = useState<TLoadDataResult>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData(httpApi)
      .then(result => setResult(result))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const updateOffer = useCallback(
    async (upd: IUpdateOffer) => {
      /* istanbul ignore if */
      if (!result || result === 'error' || result === 'no_access') {
        return;
      }

      const offer = result.offers.find(o => o.id === upd.id);
      /* istanbul ignore if */
      if (!offer) {
        return;
      }

      await updateData(httpApi, upd);

      offer.status = upd.status;
      offer.comment = upd.comment;
      setResult({ offers: result.offers });
    },
    [result, setResult, httpApi],
  );

  return { result, loading, updateOffer };
}
